import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Notebook } from 'lucide-react';
import { cn } from '../lib/utils';

interface NotesPopupProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string | undefined;
  viewTitle: string;
  triggerRef?: React.RefObject<HTMLElement | null>; // Reference to the button that triggered this
  position?: { top?: number; right?: number; bottom?: number; left?: number }; // Optional position override
}

/** Gap between the trigger button and the popup. */
const TRIGGER_GAP = 8;
/** Smallest gap the popup keeps from the edge of the viewport. */
const EDGE_GAP = 8;
/** Half the arrow's height, used to keep it inside the popup's rounded corners. */
const ARROW_HALF = 6;
/** Width used before the popup has been measured; matches the `w-80` class. */
const FALLBACK_WIDTH = 320;

interface Placement {
  top: number;
  left: number;
  side: 'right' | 'left';
  /** Arrow offset from the popup's top edge, so it points at the trigger. */
  arrowTop: number;
}

export function NotesPopup({ isOpen, onClose, notes, viewTitle, triggerRef, position: positionProp }: NotesPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  // Kept separate from `placement` so the fade-in runs once per open, rather
  // than restarting every time a scroll nudges the popup.
  const [isVisible, setIsVisible] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  /**
   * Place the popup beside its trigger, clamped to the viewport.
   *
   * It prefers the left of the button, which is where it sits for cards in the
   * right-hand columns. For a card in a left-hand column there is no room
   * there — the popup used to be positioned off the left edge of the screen
   * (at a 1100px viewport, 174px of it was unreachable), so it flips to the
   * right of the button, and only clamps if neither side fits.
   */
  const updatePosition = useCallback(() => {
    const trigger = triggerRef?.current;
    if (!trigger) {
      setPlacement(null);
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const popup = popupRef.current;
    const width = popup?.offsetWidth || FALLBACK_WIDTH;
    const height = popup?.offsetHeight ?? 0;
    const { innerWidth: vw, innerHeight: vh } = window;

    let left = rect.left - TRIGGER_GAP - width;
    let side: 'right' | 'left' = 'right';

    if (left < EDGE_GAP) {
      const flipped = rect.right + TRIGGER_GAP;
      if (flipped + width <= vw - EDGE_GAP) {
        left = flipped;
        side = 'left';
      } else {
        left = Math.max(EDGE_GAP, vw - EDGE_GAP - width);
      }
    }

    // Centre the popup on the trigger rather than top-aligning it, then clamp.
    // Top-aligning pushed the body of long notes well below the button it came
    // from, which is what made the popup feel unrelated to the card.
    const triggerCentre = rect.top + rect.height / 2;
    let top = triggerCentre - height / 2;
    if (height > 0) {
      top = Math.min(top, vh - EDGE_GAP - height);
    }
    top = Math.max(EDGE_GAP, top);

    // After clamping, the arrow has to move instead of the popup, or it stops
    // pointing at the button.
    const arrowTop = height > 0
      ? Math.min(Math.max(triggerCentre - top, ARROW_HALF + 4), height - ARROW_HALF - 4)
      : ARROW_HALF + 4;

    // `position: fixed` is only viewport-relative when no ancestor is a
    // containing block, and several hosts here are (a card's `backdrop-blur`,
    // the dialog's `translate`). Rather than special-case each one, compare
    // where the popup actually rendered against the inset we asked for and
    // fold the difference back in — one pass converges, and the scroll
    // listener keeps it honest afterwards.
    let offsetX = 0;
    let offsetY = 0;
    if (popup) {
      const current = popup.getBoundingClientRect();
      offsetX = (parseFloat(popup.style.left) || 0) - current.left;
      offsetY = (parseFloat(popup.style.top) || 0) - current.top;
    }

    setPlacement({ top: top + offsetY, left: left + offsetX, side, arrowTop });
  }, [triggerRef]);

  // Resolve the portal target from the trigger, before the popup is placed.
  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }
    const trigger = triggerRef?.current ?? null;
    setPortalContainer(trigger?.closest<HTMLElement>('[role="dialog"]') ?? document.body);
  }, [isOpen, triggerRef]);

  // Position before the first paint, so the popup is never shown unplaced.
  useLayoutEffect(() => {
    if (!isOpen || positionProp) {
      setPlacement(null);
      return;
    }
    updatePosition();
    // A second pass once the content has laid out, so the vertical centring,
    // the arrow and the containing-block correction use real measurements
    // rather than the pre-measure zeroes.
    updatePosition();
  }, [isOpen, positionProp, updatePosition, notes, portalContainer]);

  // Fade in on the frame after placement, so the popup never animates from a
  // stale or unmeasured position — it used to appear to fly in from off-screen.
  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return;
    }
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // The popup is `fixed`, so without this it stays where it was opened while
  // the card scrolls out from under it.
  useEffect(() => {
    if (!isOpen || positionProp) {
      return;
    }

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updatePosition);
    };

    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);

    // Catches the popup growing or shrinking (long notes, a font swap) without
    // waiting for a scroll to re-measure it.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (observer && popupRef.current) {
      observer.observe(popupRef.current);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [isOpen, positionProp, updatePosition]);

  // Move focus into the popup when it opens and hand it back to the trigger
  // when it closes, so the shortcut is usable from the keyboard.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const trigger = triggerRef?.current ?? null;
    popupRef.current?.focus();

    return () => {
      if (trigger && document.body.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [isOpen, triggerRef]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      // Only handle escape if this popup is open AND we're not in a focused input
      if (e.key === 'Escape' && isOpen && 
          !(document.activeElement instanceof HTMLInputElement) &&
          !(document.activeElement instanceof HTMLTextAreaElement)) {
        e.stopPropagation(); // Prevent other listeners from interfering
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen || !popupRef.current) {
        return;
      }
      const target = e.target as Node;
      // Clicks on the trigger are left to the trigger's own handler, so the
      // button toggles instead of closing here and immediately reopening.
      if (popupRef.current.contains(target) || triggerRef?.current?.contains(target)) {
        return;
      }
      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  // Use prop position if provided, otherwise use the measured placement
  const finalPosition = positionProp ?? (placement ? { top: placement.top, left: placement.left } : {});

  // With no anchor to measure, a `fixed` element with no insets renders at its
  // static position, which is usually off-screen. Centre it instead so the
  // popup is always reachable.
  const isUnpositioned =
    finalPosition.top === undefined &&
    finalPosition.right === undefined &&
    finalPosition.bottom === undefined &&
    finalPosition.left === undefined;

  const popup = (
    <div
      ref={popupRef}
      role="dialog"
      aria-label={`Notes for ${viewTitle}`}
      tabIndex={-1}
      // A portal still bubbles React events to the component that rendered it,
      // so without these a click or Enter inside the popup would reach the
      // card's own click handler and open the full view modal behind it.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        top: finalPosition.top !== undefined ? `${finalPosition.top}px` : undefined,
        right: finalPosition.right !== undefined ? `${finalPosition.right}px` : undefined,
        bottom: finalPosition.bottom !== undefined ? `${finalPosition.bottom}px` : undefined,
        left: finalPosition.left !== undefined ? `${finalPosition.left}px` : undefined,
        // Only opacity and transform animate. Transitioning `top`/`left` made
        // the popup slide across the screen on every scroll re-measure.
        transition: 'opacity 150ms ease-out',
        // Opacity only: a scale transform would skew the rect the placement
        // correction above measures.
        opacity: isVisible ? 1 : 0,
      }}
      className={cn(
        "fixed z-[99999999] w-80 max-w-[calc(100vw-32px)] pointer-events-auto",
        "focus:outline-none",
        isUnpositioned && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      )}
      data-testid="notes-popup"
    >
      {/* Arrow pointing at the trigger button, on whichever side it ended up */}
      {!isUnpositioned && placement && (
        <div
          style={{ top: `${placement.arrowTop}px`, marginTop: `-${ARROW_HALF}px` }}
          className={cn(
            "absolute w-0 h-0 border-t-[6px] border-b-[6px] border-t-transparent border-b-transparent",
            placement.side === 'right'
              ? "-right-2 border-l-[6px] border-l-[#020812]/95"
              : "-left-2 border-r-[6px] border-r-[#020812]/95"
          )}
        />
      )}

      <div className="bg-[#020812]/95 backdrop-blur-md rounded-lg border border-primary/20 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-primary/20 bg-gradient-to-r from-[#020812] to-[#0a1425]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 flex items-center justify-center rounded-full bg-primary/10 text-primary border border-primary/20">
                <Notebook className="h-3 w-3" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Notes</h3>
                <p className="text-xs text-primary/70 line-clamp-1">{viewTitle}</p>
              </div>
            </div>
            
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full bg-[#020812]/80 backdrop-blur-sm border border-white/10 text-white 
                         hover:bg-[#020812]/95 hover:border-primary/30 hover:text-primary transition-all duration-300"
              aria-label="Close notes"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[200px] overflow-y-auto overscroll-contain">
          {notes && notes.trim() ? (
            <div className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">
              {notes}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="h-8 w-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/5 text-primary/40 border border-primary/10">
                <Notebook className="h-4 w-4" />
              </div>
              <p className="text-xs text-muted-foreground">
                No notes available for this view.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Portalled out of the card: a card root carries `backdrop-blur`, and a
  // backdrop filter makes that element the containing block for `fixed`
  // descendants, so the popup's viewport coordinates were resolved against the
  // card and it landed off-screen. Inside a Radix dialog the portal stays
  // within the dialog content instead — the body is `pointer-events: none` and
  // focus-trapped while a modal is open, so a body-level popup would be dead.
  return createPortal(popup, portalContainer ?? document.body);
}
